/**
 * Lector de Gramps XML (probado con 1.7.2, el formato nativo de Gramps 6.0).
 *
 * Lo que la aplicacion usa se convierte al modelo; todo lo demas se guarda
 * serializado tal cual para poder devolverlo intacto al exportar.
 */
import {
  type Apellido,
  type Arbol,
  type Crudo,
  type Evento,
  type Familia,
  type Fecha,
  type Lugar,
  type Nombre,
  type NombreLugar,
  type Otros,
  type Persona,
  type RefEvento,
  type RefHijo,
  type Sexo,
} from "../model.js";
import {
  atrib,
  atribsRestantes,
  hijo,
  hijosElem,
  nombreLocal,
  parsearXml,
  serializarNodo,
  textoHijo,
  todosAtribs,
  type Elem,
} from "./dom.js";
import {
  ETIQUETAS_FECHA,
  MODELADOS_EVENT,
  MODELADOS_FAMILY,
  MODELADOS_NAME,
  MODELADOS_PERSON,
  MODELADOS_PLACEOBJ,
  SECCIONES_MODELADAS,
} from "./orden.js";

const ES_FECHA = new Set<string>(ETIQUETAS_FECHA);

/** Avisos no bloqueantes que surgen al leer. */
export interface AvisoLectura {
  mensaje: string;
  /** id visible del objeto afectado, si se sabe. */
  objeto?: string;
}

export interface ResultadoLectura {
  arbol: Arbol;
  avisos: AvisoLectura[];
}

// ---------------------------------------------------------------------------
// Auxiliares
// ---------------------------------------------------------------------------

/** Agrupa en `otros` los hijos cuyo nombre no esta en la lista de modelados. */
function recogerOtros(el: Elem, modelados: readonly string[]): Otros {
  const otros: Otros = {};
  for (const h of hijosElem(el)) {
    const n = nombreLocal(h);
    if (modelados.includes(n)) continue;
    (otros[n] ??= []).push(serializarNodo(h));
  }
  return otros;
}

/** La fecha de un elemento, en cualquiera de sus cuatro formas. */
function leerFecha(el: Elem): Fecha | undefined {
  for (const h of hijosElem(el)) {
    const n = nombreLocal(h);
    if (ES_FECHA.has(n)) return { etiqueta: n, atribs: todosAtribs(h) };
  }
  return undefined;
}

function leerApellido(el: Elem): Apellido {
  return {
    texto: el.textContent ?? "",
    prim: atrib(el, "prim") === "1",
    derivation: atrib(el, "derivation"),
    prefix: atrib(el, "prefix"),
    connector: atrib(el, "connector"),
    otrosAtribs: atribsRestantes(el, ["prim", "derivation", "prefix", "connector"]),
  };
}

function leerNombre(el: Elem): Nombre {
  return {
    atribs: todosAtribs(el),
    first: textoHijo(el, "first"),
    call: textoHijo(el, "call"),
    apellidos: hijosElem(el)
      .filter((h) => nombreLocal(h) === "surname")
      .map(leerApellido),
    suffix: textoHijo(el, "suffix"),
    title: textoHijo(el, "title"),
    nick: textoHijo(el, "nick"),
    familynick: textoHijo(el, "familynick"),
    group: textoHijo(el, "group"),
    fecha: leerFecha(el),
    otros: recogerOtros(el, MODELADOS_NAME),
  };
}

function leerRefEvento(el: Elem): RefEvento {
  return {
    hlink: atrib(el, "hlink") ?? "",
    role: atrib(el, "role"),
    otrosAtribs: atribsRestantes(el, ["hlink", "role"]),
    otros: recogerOtros(el, []),
  };
}

function leerRefHijo(el: Elem): RefHijo {
  return {
    hlink: atrib(el, "hlink") ?? "",
    mrel: atrib(el, "mrel"),
    frel: atrib(el, "frel"),
    otrosAtribs: atribsRestantes(el, ["hlink", "mrel", "frel"]),
    otros: recogerOtros(el, []),
  };
}

const SEXOS = new Set(["M", "F", "U", "X"]);

// ---------------------------------------------------------------------------
// Objetos de primer nivel
// ---------------------------------------------------------------------------

function leerPersona(el: Elem, avisos: AvisoLectura[]): Persona {
  const id = atrib(el, "id");
  const bruto = (textoHijo(el, "gender") ?? "U").toUpperCase();
  if (!SEXOS.has(bruto)) {
    avisos.push({
      objeto: id,
      mensaje: `sexo "${bruto}" no reconocido; se trata como desconocido (U)`,
    });
  }
  const sexo = (SEXOS.has(bruto) ? bruto : "U") as Sexo;

  return {
    handle: atrib(el, "handle") ?? "",
    id,
    change: atrib(el, "change") ?? "0",
    priv: atrib(el, "priv"),
    sexo,
    nombres: hijosElem(el)
      .filter((h) => nombreLocal(h) === "name")
      .map(leerNombre),
    eventos: hijosElem(el)
      .filter((h) => nombreLocal(h) === "eventref")
      .map(leerRefEvento),
    childof: hijosElem(el)
      .filter((h) => nombreLocal(h) === "childof")
      .map((h) => atrib(h, "hlink") ?? ""),
    parentin: hijosElem(el)
      .filter((h) => nombreLocal(h) === "parentin")
      .map((h) => atrib(h, "hlink") ?? ""),
    otrosAtribs: atribsRestantes(el, ["handle", "id", "change", "priv"]),
    otros: recogerOtros(el, MODELADOS_PERSON),
  };
}

function leerFamilia(el: Elem): Familia {
  const rel = hijo(el, "rel");
  const padre = hijo(el, "father");
  const madre = hijo(el, "mother");
  return {
    handle: atrib(el, "handle") ?? "",
    id: atrib(el, "id"),
    change: atrib(el, "change") ?? "0",
    priv: atrib(el, "priv"),
    rel: rel ? (atrib(rel, "type") ?? "Unknown") : undefined,
    padre: padre ? atrib(padre, "hlink") : undefined,
    madre: madre ? atrib(madre, "hlink") : undefined,
    eventos: hijosElem(el)
      .filter((h) => nombreLocal(h) === "eventref")
      .map(leerRefEvento),
    hijos: hijosElem(el)
      .filter((h) => nombreLocal(h) === "childref")
      .map(leerRefHijo),
    otrosAtribs: atribsRestantes(el, ["handle", "id", "change", "priv"]),
    otros: recogerOtros(el, MODELADOS_FAMILY),
  };
}

function leerEvento(el: Elem): Evento {
  const lugar = hijo(el, "place");
  return {
    handle: atrib(el, "handle") ?? "",
    id: atrib(el, "id"),
    change: atrib(el, "change") ?? "0",
    priv: atrib(el, "priv"),
    tipo: textoHijo(el, "type"),
    fecha: leerFecha(el),
    lugar: lugar ? atrib(lugar, "hlink") : undefined,
    descripcion: textoHijo(el, "description"),
    otrosAtribs: atribsRestantes(el, ["handle", "id", "change", "priv"]),
    otros: recogerOtros(el, MODELADOS_EVENT),
  };
}

function leerNombreLugar(el: Elem): NombreLugar {
  return {
    value: atrib(el, "value") ?? "",
    lang: atrib(el, "lang"),
    fecha: leerFecha(el),
    otrosAtribs: atribsRestantes(el, ["value", "lang"]),
  };
}

function leerLugar(el: Elem): Lugar {
  return {
    handle: atrib(el, "handle") ?? "",
    id: atrib(el, "id"),
    change: atrib(el, "change") ?? "0",
    priv: atrib(el, "priv"),
    tipo: atrib(el, "type") ?? "Unknown",
    titulo: textoHijo(el, "ptitle"),
    codigo: textoHijo(el, "code"),
    nombres: hijosElem(el)
      .filter((h) => nombreLocal(h) === "pname")
      .map(leerNombreLugar),
    otrosAtribs: atribsRestantes(el, ["handle", "id", "change", "priv", "type"]),
    otros: recogerOtros(el, MODELADOS_PLACEOBJ),
  };
}

// ---------------------------------------------------------------------------
// Entrada principal
// ---------------------------------------------------------------------------

/** Deduce la version del formato a partir del xmlns, o del DOCTYPE. */
function detectarVersion(doc: Document, texto: string): string {
  const ns = doc.documentElement?.getAttribute("xmlns") ?? "";
  const m = /xml\/(\d+\.\d+\.\d+)\//.exec(ns);
  if (m?.[1]) return m[1];
  const d = /DTD Gramps XML (\d+\.\d+\.\d+)/i.exec(texto);
  return d?.[1] ?? "1.7.2";
}

/**
 * Lee un fichero Gramps XML completo.
 *
 * Lanza si el XML esta mal formado o si la raiz no es <database>.
 * Los problemas recuperables salen como avisos.
 */
export function leerGramps(texto: string): ResultadoLectura {
  const doc = parsearXml(texto);
  const raiz = doc.documentElement as Elem;
  if (nombreLocal(raiz) !== "database") {
    throw new Error(
      `la raíz es <${nombreLocal(raiz)}>, se esperaba <database>: ` +
        "esto no parece un fichero Gramps XML",
    );
  }

  const avisos: AvisoLectura[] = [];
  const version = detectarVersion(doc, texto);
  if (!version.startsWith("1.7")) {
    avisos.push({
      mensaje:
        `el fichero declara Gramps XML ${version}; esta versión se ha ` +
        "probado con 1.7.x, revisa el resultado",
    });
  }

  const arbol: Arbol = {
    version,
    cabecera: { creadoFecha: "", creadoVersion: "", otros: {} },
    personas: new Map(),
    familias: new Map(),
    eventos: new Map(),
    lugares: new Map(),
    atribsPeople: {},
    seccionesCrudas: {},
  };

  for (const sec of hijosElem(raiz)) {
    const nombre = nombreLocal(sec);

    if (nombre === "header") {
      const creado = hijo(sec, "created");
      arbol.cabecera = {
        creadoFecha: creado ? (atrib(creado, "date") ?? "") : "",
        creadoVersion: creado ? (atrib(creado, "version") ?? "") : "",
        otros: recogerOtros(sec, ["created"]),
      };
      continue;
    }

    if (!(SECCIONES_MODELADAS as readonly string[]).includes(nombre)) {
      // Seccion que no modelamos: se guarda entera y se devuelve igual.
      arbol.seccionesCrudas[nombre] = serializarNodo(sec) as Crudo;
      continue;
    }

    if (nombre === "people") arbol.atribsPeople = todosAtribs(sec);

    for (const obj of hijosElem(sec)) {
      switch (nombreLocal(obj)) {
        case "person": {
          const p = leerPersona(obj, avisos);
          if (arbol.personas.has(p.handle)) {
            avisos.push({ objeto: p.id, mensaje: "handle repetido; se ignora la segunda" });
            break;
          }
          arbol.personas.set(p.handle, p);
          break;
        }
        case "family": {
          const f = leerFamilia(obj);
          arbol.familias.set(f.handle, f);
          break;
        }
        case "event": {
          const e = leerEvento(obj);
          arbol.eventos.set(e.handle, e);
          break;
        }
        case "placeobj": {
          const l = leerLugar(obj);
          arbol.lugares.set(l.handle, l);
          break;
        }
        default: {
          // Un objeto inesperado dentro de una seccion conocida: lo dejamos
          // caer en la seccion cruda para no perderlo.
          const clave = `${nombre}:extra`;
          arbol.seccionesCrudas[clave] =
            (arbol.seccionesCrudas[clave] ?? "") + serializarNodo(obj);
        }
      }
    }
  }

  return { arbol, avisos };
}
